// src/pages/seeker/SeekerAttributes.jsx

import React, { useEffect, useState } from 'react';
import { seekerAPI } from '../../services/api';
import { Spinner, usePersistedState } from '../../components/shared/UI';
import { ChevronUp, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';

// slug-ify section title for persistence key
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const C = { navy: '#0e4e95', blue: '#3D80CC', text: '#333', muted: '#888', border: '#777', white: '#fff', offwhite: '#f5f5f5' };

const ATTRIBUTE_SECTIONS = [
  { title: 'Cognitive Abilities', items: [
    'Logical reasoning', 'Abstract thinking', 'Spatial awareness', 'Pattern recognition',
    'Memory retention', 'Processing speed', 'Concentration/focus', 'Critical thinking',
    'Creative thinking', 'Problem solving', 'Decision making', 'Mental flexibility/adaptability',
    'Numerical reasoning', 'Verbal reasoning', 'Inductive reasoning', 'Deductive reasoning',
  ]},
  { title: 'Physical Abilities', items: [
    'Strength', 'Endurance/stamina', 'Flexibility', 'Agility', 'Balance', 'Coordination',
    'Reaction time', 'Manual dexterity', 'Fine motor control', 'Gross motor control',
    'Speed', 'Hand-eye coordination',
  ]},
  { title: 'Sensory Abilities', items: [
    'Visual acuity', 'Colour perception', 'Depth perception', 'Auditory discrimination',
    'Pitch recognition', 'Spatial hearing', 'Tactile sensitivity',
  ]},
  { title: 'Interpersonal & Emotional Abilities', items: [
    'Empathy', 'Emotional regulation', 'Reading social cues', 'Intuition', 'Self-awareness',
    'Persuasiveness', 'Charisma', 'Patience', 'Resilience', 'Stress tolerance', 'Impulse control',
  ]},
  { title: 'Creative & Perceptual Abilities', items: [
    'Aesthetic sensitivity', 'Musical aptitude', 'Linguistic aptitude',
    'Visual-spatial imagination', 'Originality/novelty generation',
  ]},
  { title: 'Organisational & Cognitive Style', items: [
    'Attention to detail', 'Big picture thinking', 'Multi-tasking capacity',
    'Systematic thinking', 'Lateral thinking',
  ]},
];

export { ATTRIBUTE_SECTIONS };

const AttributeSection = ({ section, selected, onToggle, blue, textColor }) => {
  const [open, setOpen] = usePersistedState(`tikhet:seeker:attributes:${slug(section.title)}:open`, true);
  return (
    <div className="section-box" style={{ marginBottom: 16 }}>
      <div
        className="section-box-header"
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(o => !o)}
      >
        <span style={{ flex: 1 }}>{section.title}</span>
        {open ? <ChevronUp size={14} style={{ opacity: 0.85 }} /> : <ChevronDown size={14} style={{ opacity: 0.85 }} />}
      </div>
      {open && (
        <div className="section-box-body" style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {section.items.map(item => {
              const checked = selected.has(item);
              return (
                <label key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '4px 0' }}>
                  <input type="checkbox" checked={checked} onChange={() => onToggle(item)}
                    style={{ accentColor: blue, width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: textColor }}>{item}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const SeekerAttributes = () => {
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    seekerAPI.getProfile()
      .then(({ data }) => {
        const attrs = data.data?.attributes;
        if (Array.isArray(attrs)) setSelected(new Set(attrs));
      })
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await seekerAPI.updateProfile({ attributes: [...selected] });
      toast.success('Attributes saved');
    } catch { /* interceptor toasts */ }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>Attributes</h1>
      </div>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
        Select the attributes that describe your abilities. These are used in matching with employer requirements.
      </p>

      {ATTRIBUTE_SECTIONS.map((section, si) => (
        <AttributeSection
          key={si}
          section={section}
          selected={selected}
          onToggle={toggle}
          blue={C.blue}
          textColor={C.text}
        />
      ))}

      <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
        <button onClick={handleSave} disabled={saving}
          className="btn btn-success" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {saving ? <Spinner size={16} /> : 'Save Attributes'}
        </button>
      </div>
    </div>
  );
};
